import React from "react";
import styled from "styled-components";
import {
  APP_NAME,
  APP_SUPPORT_EMAIL,
  APP_URL,
  FULL_COMPANY_NAME,
  TAGLINE,
} from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import SEO from "../components/SEO";

export default function PrivacyPolicy() {
  return (
    <Container>
      <SEO
        title={`Privacy Policy | ${APP_NAME} - ${TAGLINE}`}
        description={`${APP_NAME} Privacy Policy - Learn how we protect your data and maintain privacy compliance for our AI-powered property management platform used by landlords and property managers.`}
        keywords={`${APP_NAME} privacy policy, property management platform privacy, landlord software data protection, tenant data security, AI property management privacy`}
        imageAlt={`${APP_NAME} privacy policy and data protection`}
        category="Legal"
        tags={[
          "LandlordOps privacy policy",
          "property management platform privacy",
          "landlord software data protection",
          "tenant data security",
        ]}
      />
      <MainHeader>Privacy Policy</MainHeader>
      <Spacer $marginTop="2rem" />
      <TextContainer>
        <TextSection>
          {APP_NAME} Services ("{APP_NAME}" or "Services") are provided by{" "}
          {FULL_COMPANY_NAME} ("We", "Our", "{APP_NAME}") to you ("User" or
          "Users").
        </TextSection>
        <TextSection>
          We believe in transparency and care about your privacy. We want you to
          be clear how we're using your information as you use our Services, so
          please take the time to read this policy carefully.
        </TextSection>
        <TextSection>
          <strong>Last Updated:</strong> July 24, 2026
        </TextSection>

        <Header>Subscription tiers and feature scope</Header>
        <TextSection>
          {APP_NAME} may offer a <strong>Free Gateway</strong> (limited doors, no
          paid subscription), self-serve paid plans such as Starter, Growth,
          and Scale, and quote-based <strong>Enterprise</strong> offerings.
          Current public self-serve plans include the full LandlordOps
          operating system, including OpsChat tenant messaging, Leo AI, in-app
          rent collection, vendor workflows, books and accounting exports,
          listing and leasing tools where available, and related dashboards.
          OpsChat is a tenant-facing feature within LandlordOps, not a separate
          product line or subscription. Public self-serve plans differ primarily
          by door or unit limits. We may also support{" "}
          <strong>monthly and annual</strong> billing, promotional pricing, and
          legacy or grandfathered catalogs for some accounts. Feature
          availability may vary for legacy or grandfathered accounts,
          Enterprise or custom agreements, beta features, third-party service
          availability, and account configuration. The categories of
          information we process depend on which features you use and enable.
        </TextSection>
        <TextSection>
          Features that involve <strong>tenants or prospects</strong> (for
          example, Magic Link access, public or embeddable chat, pre-screening
          flows, public rental application forms, lease-up / onboarding
          pipelines, and in-product message threads) may process tenant or
          prospect content, contact details, identity and income information,
          uploaded documents, and related metadata. Features that involve{" "}
          <strong>documents and knowledge</strong> (for example, lease uploads,
          house rules, and knowledge bases used to ground AI responses) process
          the files and text you choose to provide. Financial, rent, vendor, and
          other property-operating features may process additional categories
          of data as described throughout this policy when you use or enable
          them.
        </TextSection>

        <Header>Document Templates &amp; Legal Forms</Header>
        <TextSection>
          {APP_NAME} provides technology for document automation. We are not a
          law firm. Lease templates are provided for informational purposes
          only. Users must have documents reviewed by their own legal counsel.
          For full terms, see our <a href="/terms">Terms of Service</a>.
        </TextSection>

        <Header>Privacy Compliance Statement</Header>
        <TextSection>
          As a provider of comprehensive AI-powered property management
          technology for landlords, property managers, and real estate
          investors, {APP_NAME} is committed to complying with all applicable
          privacy laws and regulations, including but not limited to the
          California Consumer Privacy Act (CCPA), General Data Protection
          Regulation (GDPR), and other state and federal privacy laws. We have
          implemented comprehensive safeguards to protect personal information,
          including tenant data and property information.
        </TextSection>

        <Header>Legal Advice Disclaimer (Privacy Context)</Header>
        <TextSection>
          From time to time, the {APP_NAME} platform or communications may
          include information about landlord-tenant laws or legal processes in
          order to explain how personal information may be used within property
          management workflows (for example, notices, lease terms, or compliance
          topics). Any such information is provided strictly for informational
          purposes only and should not be construed as legal advice. {APP_NAME}{" "}
          is not a law firm and does not provide legal services. You should
          always consult with a qualified attorney regarding your specific
          situation, compliance obligations, and any legal questions. Your use
          of our Services does not create an attorney-client relationship.
        </TextSection>
        <TextSection>
          We do not request or require that tenants waive any of their privacy
          rights to use our Services. Instead, landlords and property managers
          using our platform are responsible for obtaining appropriate tenant
          authorizations that permit the specific uses of personal information
          required for our property management services.
        </TextSection>
        <TextSection>
          For more information about our privacy practices, please visit{" "}
          <a
            href={`https://${APP_URL}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}/privacy
          </a>
          .
        </TextSection>

        <Header>Information We Collect or Receive About You</Header>
        <TextSection>
          We collect and process information necessary to provide our
          comprehensive property management platform Services. This includes
          both standard business information and tenant information when used by
          landlords and property managers.
        </TextSection>
        <TextSection>
          The {APP_NAME} platform consists of multiple integrated components
          that may collect different types of information to provide our
          services:
        </TextSection>
        <TextSection>
          <strong>Rent Tracking System:</strong> When you use our rent tracking
          system, we collect tenant payment information, due dates, payment
          history, late fee calculations, and financial records to provide
          accurate rent tracking and financial reporting. This information may
          include tenant names, payment amounts, dates, and property-specific
          financial data.
        </TextSection>
        <TextSection>
          <strong>Leasing Workflows:</strong> When landlords use leasing-related
          features (for example, storing lease terms, renewal dates, or leasing
          notes), we may process property information and lease-related
          information to support those workflows. {APP_NAME} does not generate
          lease documents or legal documents.
        </TextSection>
        <TextSection>
          <strong>Repair Management System:</strong> Our repair tracking system
          collects maintenance request details, tenant communications, repair
          status updates, contractor information, and property maintenance
          history to help landlords manage property repairs efficiently.
        </TextSection>
        <TextSection>
          <strong>AI assistants (including Leo) and informational Q&amp;A:</strong>{" "}
          Our AI assistants collect questions, responses, and interaction data
          to provide <em>general informational</em> help about property
          management topics, product how-tos, and common scenarios. This may
          include landlord-tenant topics described in a purely informational
          way. Assistants are not lawyer chatbots: outputs are not legal
          advice. This includes state-specific or situational questions you
          may ask in chat or in-product help surfaces.
        </TextSection>
        <TextSection>
          <strong>
            OpsChat tenant messaging and your AI First Responder:
          </strong>{" "}
          When you use tenant-facing chat, email or SMS/voice triage, or
          similar messaging features, we process the content of those
          communications, delivery metadata, and account configuration (for
          example, rules, knowledge-base text, and uploaded leases or documents
          you link to a unit) as needed to operate triage, escalation, and audit
          trails. Tenants and prospects interact with the Services under
          notices and settings you configure.
        </TextSection>
        <TextSection>
          <strong>Property Analytics Dashboard:</strong> Our analytics system
          collects property performance data, rent collection metrics, vacancy
          rates, maintenance costs, ROI calculations, and platform usage
          statistics to help landlords optimize their property management and
          investment decisions.
        </TextSection>
        <TextSection>
          <strong>AI Data Processing:</strong> Our artificial intelligence
          systems process various types of data to provide intelligent
          responses, generate messages and summaries, and improve platform
          performance. This includes analyzing property management patterns,
          user interaction data for platform optimization, and anonymized usage
          patterns for AI model training.
        </TextSection>
        <TextSection>
          <strong>Tax Preparation Support:</strong> When you use our tax
          preparation feature, we collect the information you voluntarily
          submit, including rental property addresses, total rents collected,
          expense amounts and categories, your name, and your email address. We
          may store this data temporarily in your browser (local storage) for
          draft purposes. When you submit, we transmit this data to your
          designated certified public accountant (CPA) via email, with you
          copied on the transmission. We do not retain a copy of submitted tax
          prep data on our servers beyond what is necessary to complete the
          transmission.
        </TextSection>
        <TextSection>
          <strong>Deal Analyzer:</strong> When you use the Deal Analyzer
          feature, we process the address or listing URL (from any platform) you
          submit to generate estimated DSCR, rent, debt service, and
          informational recommendations. This may involve sending your input to
          our AI service provider to produce analysis. Analysis outputs are for
          your informational use only and do not constitute investment or
          financial advice. See our Terms of Service for full disclaimers.
        </TextSection>
        <TextSection>
          <strong>Product feedback, surveys, and optional communications:</strong>{" "}
          When you use our in-product feedback tools, contact forms, or optional
          product or onboarding surveys, we collect the information you choose to
          submit (such as message text, optional star ratings, feedback category,
          and your contact details if you provide them). To help us diagnose
          issues, understand usage patterns, and improve reliability, we may also
          collect technical and contextual information such as whether you are on
          a mobile or desktop device, browser type and version (for example, via
          User-Agent or similar browser signals), language or locale settings,
          and approximate location derived from your IP address (such as city,
          region, or country). Unless a feature explicitly requests precise device
          location with your permission, we do not use GPS-level coordinates for
          these purposes. Feedback and related metadata may be stored in our
          databases (for example, cloud-hosted storage) and may be delivered to
          our team by email or internal tools. We use this information to
          operate, secure, debug, and improve the Services.
        </TextSection>

        <ItalicsHeader>Tenant and Property Information</ItalicsHeader>
        <TextSection>
          When landlords and property managers use our Services, they may upload
          or input information that constitutes personal information under
          applicable privacy laws, including:
        </TextSection>
        <List>
          <ListItem>
            Tenant names, contact information, and payment records for rent
            tracking purposes
          </ListItem>
          <ListItem>
            Property addresses, characteristics, and management details required
            for accurate property management and leasing workflows
          </ListItem>
          <ListItem>
            Financial information relevant to rent collection, expense tracking,
            and property investment analysis
          </ListItem>
          <ListItem>
            Lease terms, property preferences, and management notes
          </ListItem>
        </List>
        <TextSection>
          All personal information is collected, stored, and processed in
          accordance with applicable privacy laws and regulations.
        </TextSection>

        <ItalicsHeader>Information you provide</ItalicsHeader>
        <TextSection>
          We collect information you provide when you create a {APP_NAME}{" "}
          account, upload content, or otherwise use the Service, including:
        </TextSection>
        <List>
          <ListItem>
            Business information: For landlords and property managers, we
            collect business name, address, contact information, licensing
            information, and property portfolio details.
          </ListItem>
          <ListItem>
            Authentication credentials: Email address, username, and other
            credentials used to access the platform.
          </ListItem>
          <ListItem>
            Payment information: For paid subscriptions, we collect payment
            details necessary to process transactions.
          </ListItem>
          <ListItem>
            Referral program information: For users participating in our
            referral programs, we collect referral links, tracking data, and
            commission information to process rewards and payments.
          </ListItem>
          <ListItem>
            Agent partnership information: For real estate agents participating
            in our partnership program, we collect professional licensing
            information, brokerage details, and commission tracking data.
          </ListItem>
        </List>

        <TextSection>
          When you create or edit listings, upload photos, or add Leo-specific
          private information (such as criteria used to validate prospects), we
          treat that data as sensitive landlord content. These details power the
          listing creation workflow, the Leo pre-screening chatbot, and any
          optional background or identity verification checks you authorize. We
          may share that data with third-party partners (for example,
          background-check providers) only when you explicitly invoke those
          services and only to the extent necessary to fulfill the screening
          request.
        </TextSection>

        <ItalicsHeader>
          Information we collect from your use of the Service
        </ItalicsHeader>
        <TextSection>
          We collect information about you and the devices you use to access the
          Service, such as your mobile phone, or tablet.
        </TextSection>

        <TextSection>The information that we collect includes:</TextSection>
        <List>
          <ListItem>
            Device information: we may collect device-specific information (such
            as your hardware model, operating system version, and unique device
            identifiers). {APP_NAME} may associate your device identifiers with
            your {APP_NAME} user account.
          </ListItem>
          <ListItem>
            Log information. When you use our services or view content provided
            on the service, we may automatically collect and store certain
            information in server logs. This may include:
          </ListItem>
          <List>
            <ListItem>Details of how you used our service.</ListItem>
            <ListItem>Internet protocol (IP) address.</ListItem>
            <ListItem>
              Device event information such as crashes, system activity,
              hardware settings, browser type, browser language, and the date
              and time of your request.
            </ListItem>
            <ListItem>
              Cookies that may uniquely identify your browser or your {APP_NAME}{" "}
              user account.
            </ListItem>
          </List>
          <ListItem>
            Location information. We may derive approximate location from your IP
            address (for example, region, city, or country) for security, fraud
            prevention, analytics, and product improvement, including when you
            submit feedback or use certain features. If a specific feature
            requests precise location from your device (for example, GPS), we
            will disclose that at the time of the request and rely on your
            consent or settings where required by law.
          </ListItem>
          <ListItem>
            Local storage. We may collect and store information (including
            personal information) locally on your device using mechanisms such
            as browser web storage (including HTML 5) and application data
            caches.
          </ListItem>
          <ListItem>
            Cookies and anonymous identifiers. We use various technologies to
            collect and store information when you visit our services, and this
            may include sending one or more cookies or anonymous identifiers to
            your device. We also use cookies and anonymous identifiers when you
            interact with services we offer to our partners, such as advertising
            services or {APP_NAME} features that may appear on other sites.
          </ListItem>
        </List>

        <Header>How We Handle and Protect Personal Information</Header>
        <TextSection>
          As a property management service provider, we have implemented the
          following safeguards for personal information:
        </TextSection>
        <List>
          <ListItem>
            <strong>Privacy Compliance:</strong> We comply with applicable
            privacy laws including CCPA, GDPR, and other state and federal
            regulations.
          </ListItem>
          <ListItem>
            <strong>Encryption:</strong> All personal information transmitted
            through our platform is encrypted in transit using TLS 1.2 or
            higher, and at rest using AES-256 bit encryption.
          </ListItem>
          <ListItem>
            <strong>Access Controls:</strong> We implement role-based access
            controls limiting personal information access to authorized
            personnel with a legitimate need.
          </ListItem>
          <ListItem>
            <strong>Audit Logs:</strong> We maintain detailed audit trails of
            all personal information access and modification activities.
          </ListItem>
          <ListItem>
            <strong>Security Risk Assessments:</strong> We conduct regular
            security risk assessments and implement appropriate safeguards.
          </ListItem>
          <ListItem>
            <strong>Data Segregation:</strong> Personal information is logically
            and physically segregated from other system data.
          </ListItem>
        </List>
        <TextSection>
          If you provide personal information to the AI assistant, it will be
          handled with the same safeguards as other personal information on our
          platform, including encryption, access controls, and compliance with
          applicable privacy laws.
        </TextSection>

        <Header>How We Use Your Information</Header>
        <TextSection>
          We rely on a number of legal bases to collect, use, or process your
          information in the ways described in this policy. These bases include
          where:
        </TextSection>
        <List>
          <ListItem>
            Necessary to provide the Services to you or perform the contractual
            obligations in our Terms of Service.
          </ListItem>
          <ListItem>
            Necessary to satisfy legal obligation, to exercise or defend legal
            claims, or to comply with a court order.
          </ListItem>
          <ListItem>
            You have consented to the processing, which you can revoke at any
            time, or
          </ListItem>
          <ListItem>You have expressly made the information public.</ListItem>
          <ListItem>
            In the case of tenant information, as permitted by applicable
            privacy laws and regulations.
          </ListItem>
        </List>

        <TextSection>
          We use information collected through our comprehensive platform to:
        </TextSection>

        <ItalicsHeader>Rent Tracking Services</ItalicsHeader>
        <List>
          <ListItem>
            Track rent payments, due dates, late fees, and payment history
            across all properties and tenants to provide accurate financial
            records
          </ListItem>
          <ListItem>
            Generate automated payment reminders, late fee calculations, and
            financial reports to help landlords maintain consistent cash flow
          </ListItem>
          <ListItem>
            Provide real-time updates on payment status, outstanding balances,
            and payment patterns to optimize rent collection processes
          </ListItem>
          <ListItem>
            Analyze payment trends, identify potential issues, and generate
            comprehensive financial reporting for tax and investment purposes
          </ListItem>
          <ListItem>
            Maintain accurate records of all financial transactions, including
            rent payments, expenses, and property-related income
          </ListItem>
          <ListItem>
            Improve rent tracking accuracy, automate payment processing, and
            enhance financial reporting through machine learning analysis
          </ListItem>
          <ListItem>
            Monitor and analyze payment performance metrics, collection rates,
            and financial trends to enhance landlord profitability and property
            management efficiency
          </ListItem>
        </List>

        <ItalicsHeader>Leasing Guidance Services</ItalicsHeader>
        <List>
          <ListItem>
            Support leasing workflows (for example, tracking lease terms,
            renewal dates, and leasing notes) within your account
          </ListItem>
          <ListItem>
            Provide general leasing guidance and educational information (not
            legal advice)
          </ListItem>
          <ListItem>
            Help landlords understand common lease considerations and next steps
            (without generating legal documents)
          </ListItem>
          <ListItem>
            Improve the quality of guidance and workflow support through
            continuous refinement and updates
          </ListItem>
        </List>

        <ItalicsHeader>
          Public rental applications and lease-up prospects
        </ItalicsHeader>
        <TextSection>
          When a landlord enables a public rental application link (or related
          listing / lease-up tools) for a vacancy, prospects may submit an
          application through {APP_NAME}. In that case, the applicant is also a
          User of the Services for purposes of this Privacy Policy with respect
          to the information they provide.
        </TextSection>
        <List>
          <ListItem>
            Collect information the applicant voluntarily submits, which may
            include identity and contact details (such as name, email, phone,
            current address, and last four digits of Social Security number),
            employment and income information, prior landlord references, and
            other application answers the form requests
          </ListItem>
          <ListItem>
            Collect documents the applicant uploads (for example, photo ID,
            paystubs, or similar supporting files) and store them in our systems
            or cloud storage so the landlord can review the application
          </ListItem>
          <ListItem>
            Create or update a draft / prospect tenant record associated with
            the landlord&apos;s account and the relevant listing or unit, and
            surface that application in lease-up / onboarding tools for the
            landlord
          </ListItem>
          <ListItem>
            Send transactional emails related to the application (for example,
            confirmation to the applicant and/or notification to the landlord),
            including limited application or property details needed to identify
            the submission
          </ListItem>
          <ListItem>
            Process consent signals recorded at submission (for example,
            agreement to these terms and privacy practices, and consent for the
            landlord to run background or credit checks outside or through
            optional third-party screening workflows)
          </ListItem>
          <ListItem>
            Share application data with the landlord (and their authorized
            account users) who requested the application. Screening, background,
            or credit checks are typically performed by the landlord or by
            optional third-party providers the landlord chooses; when such a
            provider is used, the provider&apos;s terms and privacy policy also
            apply
          </ListItem>
          <ListItem>
            Retain application and document data for as long as needed to
            operate the lease-up workflow, support the landlord&apos;s account,
            meet legal or security obligations, and resolve disputes, then
            delete or de-identify it according to our retention practices
          </ListItem>
        </List>
        <TextSection>
          Applicants should only submit information they are authorized to
          provide. Landlords are responsible for how they use application data
          (including fair housing and screening compliance). {APP_NAME} provides
          technology to collect and deliver applications; we are not the
          landlord and do not decide whom to lease to.
        </TextSection>

        <ItalicsHeader>Repair Management Services</ItalicsHeader>
        <List>
          <ListItem>
            Track repair requests, maintenance needs, and contractor
            coordination to ensure timely property maintenance
          </ListItem>
          <ListItem>
            Generate professional follow-up messages using AI to maintain clear
            communication between landlords and tenants
          </ListItem>
          <ListItem>
            Monitor repair status, track costs, and maintain maintenance history
            to optimize property upkeep and tenant satisfaction
          </ListItem>
          <ListItem>
            Coordinate with contractors, schedule repairs, and maintain detailed
            records of all maintenance activities
          </ListItem>
        </List>

        <ItalicsHeader>
          AI assistants, OpsChat tenant messaging, and informational help (not
          legal advice)
        </ItalicsHeader>
        <List>
          <ListItem>
            Provide general informational answers and product help through AI
            assistants (including Leo), including common landlord-tenant and
            property-management topics, where such features are enabled for your
            account
          </ListItem>
          <ListItem>
            Operate tenant messaging, triage, and your AI First Responder
            so that communications are routed, summarized, or escalated
            according to your settings
          </ListItem>
          <ListItem>
            Ground responses in context you provide (for example, uploaded
            leases, knowledge-base content, and unit settings), subject to your
            account configuration
          </ListItem>
          <ListItem>
            Improve reliability and safety of assistants through monitoring,
            product updates, and (where applicable) model tuning, consistent
            with this policy and your choices
          </ListItem>
        </List>

        <ItalicsHeader>Tax Preparation Services</ItalicsHeader>
        <List>
          <ListItem>
            Collect and format rental property data (addresses, income,
            expenses) you submit for transmission to your designated CPA
          </ListItem>
          <ListItem>
            Transmit your tax prep submission to your CPA via email, with you
            CC'd on the transmission
          </ListItem>
          <ListItem>
            Store draft data in your browser's local storage so you can complete
            the form over multiple sessions (this data remains on your device
            until you clear it or submit)
          </ListItem>
        </List>

        <ItalicsHeader>Deal Analyzer</ItalicsHeader>
        <List>
          <ListItem>
            Process the address or listing URL you submit to generate DSCR
            estimates, rent and debt service estimates, and informational
            recommendations
          </ListItem>
          <ListItem>
            Use AI to infer property and market data for the analysis. Outputs
            are for your informational use only and are not investment or
            financial advice
          </ListItem>
        </List>

        <ItalicsHeader>Product feedback and research</ItalicsHeader>
        <List>
          <ListItem>
            Collect and review feedback, ratings, and survey responses you
            voluntarily submit through in-product tools, email, or forms
          </ListItem>
          <ListItem>
            Associate feedback with limited technical diagnostics (such as
            browser or device type, User-Agent or similar signals, locale, and
            approximate IP-based location) to reproduce issues and prioritize
            improvements
          </ListItem>
          <ListItem>
            Store feedback records and related metadata in our systems for as
            long as needed for product quality, legal compliance, and security
          </ListItem>
        </List>

        <ItalicsHeader>Analytics and Dashboard Services</ItalicsHeader>
        <List>
          <ListItem>
            Track property performance metrics including rent collection rates,
            vacancy rates, maintenance costs, and ROI calculations to help
            landlords optimize their investments
          </ListItem>
          <ListItem>
            Provide comprehensive reporting on property performance, tenant
            interactions, financial trends, and market analysis to support
            investment decisions
          </ListItem>
          <ListItem>
            Monitor property management efficiency, identify optimization
            opportunities, and generate insights to improve landlord
            profitability
          </ListItem>
          <ListItem>
            Generate detailed ROI analytics including cost per property, tenant
            retention rates, and revenue tracking to help landlords measure
            investment performance
          </ListItem>
          <ListItem>
            Analyze market trends, seasonal patterns, and property performance
            to enhance overall investment strategy and property management
            insights
          </ListItem>
          <ListItem>
            Track property management activities, maintenance schedules, and
            tenant satisfaction metrics to help landlords improve their
            operations
          </ListItem>
        </List>

        <ItalicsHeader>
          LandlordOps operating system features
        </ItalicsHeader>
        <List>
          <ListItem>
            When you use these features, process rent collection, payment
            status, in-app financial records, vendor and maintenance workflows,
            books and categorization, Schedule E or tax-oriented exports, and
            investor or closing-readiness records (for example, closing vault or
            diligence-oriented summaries)
          </ListItem>
          <ListItem>
            Support listing, leasing, or vacancy-related workflows where
            offered, including prospect interactions that you connect to your
            account
          </ListItem>
          <ListItem>
            Apply stricter access and retention controls to high-sensitivity
            financial and identity data in line with this policy and in-product
            settings
          </ListItem>
        </List>

        <ItalicsHeader>
          Platform Integration and Technical Services
        </ItalicsHeader>
        <List>
          <ListItem>
            Ensure proper functioning of our property management platform across
            different devices and operating systems through performance
            monitoring and compatibility testing
          </ListItem>
          <ListItem>
            Process rent payments, expense tracking, financial reporting, and
            tax preparation assistance securely through PCI-compliant payment
            processors and accounting integrations, where those features are
            enabled for your account
          </ListItem>
          <ListItem>
            Maintain platform security, performance, availability, and privacy
            compliance across all integrated components with real-time
            monitoring and backup systems
          </ListItem>
          <ListItem>
            Provide technical support, implementation assistance, platform
            customization, and troubleshooting for landlord onboarding and
            ongoing platform usage
          </ListItem>
          <ListItem>
            Monitor platform performance metrics, loading times, interaction
            rates, and technical errors to ensure optimal user experience across
            all devices
          </ListItem>
          <ListItem>
            Integrate with third-party property management systems, accounting
            software, and legal services to streamline landlord workflows and
            data management
          </ListItem>
        </List>

        <ItalicsHeader>Referral and Agent Partnership Programs</ItalicsHeader>
        <List>
          <ListItem>
            Track referral links and monitor referral activity to process
            rewards and commissions for users and real estate agents
          </ListItem>
          <ListItem>
            Verify agent credentials and maintain agent partnership records to
            ensure program integrity and compliance
          </ListItem>
          <ListItem>
            Process commission payments and reward distributions to eligible
            participants in our referral programs
          </ListItem>
          <ListItem>
            Monitor referral program performance and prevent fraud through
            automated detection and manual review processes
          </ListItem>
          <ListItem>
            Maintain records of referral relationships, commission history, and
            program participation for tax and compliance purposes
          </ListItem>
        </List>

        <ItalicsHeader>Providing and improving our services</ItalicsHeader>
        <List>
          <ListItem>
            Providing operational guidance and improving property management
            workflows
          </ListItem>
          <ListItem>
            Providing, maintaining, and improving our Services, including the
            specific {APP_NAME} features you choose to use.
          </ListItem>
          <ListItem>Personalizing your use of the Services.</ListItem>
          <ListItem>Developing new products, services, and features.</ListItem>
          <ListItem>
            Determining whether the Services or specific features are suitable
            for your property management needs based on your submitted
            information.
          </ListItem>
          <ListItem>
            Measuring, tracking, and analyzing trends in usage in connection
            with your use or the performance of our Services.
          </ListItem>
        </List>

        <ItalicsHeader>Communicating with you about our services</ItalicsHeader>
        <List>
          <ListItem>
            Sending you information you have requested from us or that we think
            you may find useful, such as updates or changes to our Services.
          </ListItem>
          <ListItem>Collecting feedback about our Services.</ListItem>
          <ListItem>
            Contacting you to resolve disputes, provide assistance, or notify
            you of changes to our policies or terms.
          </ListItem>
        </List>

        <ItalicsHeader>
          Keeping our services and community safe and secure
        </ItalicsHeader>
        <List>
          <ListItem>
            Enforcing our Terms of Service or other applicable contracts,
            agreements, or policies.
          </ListItem>
          <ListItem>
            Complying with any applicable laws or regulations, including privacy
            laws, or in response to lawful requests for information from the
            government or other third party.
          </ListItem>
          <ListItem>
            Protecting against unauthorized access, use, or disclosure of
            personal information or other sensitive information.
          </ListItem>
        </List>

        <ItalicsHeader>Service providers</ItalicsHeader>
        <List>
          <ListItem>
            {APP_NAME} engages with third-party companies and service providers
            (such as analytics providers, cloud storage providers, and payment
            processors) to help us provide or improve our Services. These third
            parties have limited access to your information, and may only use
            the information provided to perform specific tasks on our behalf.
            All third-party service providers who may have access to personal
            information are bound by data processing agreements ensuring privacy
            compliance.
          </ListItem>
        </List>

        <ItalicsHeader>Aggregated information</ItalicsHeader>
        <List>
          <ListItem>
            Aggregated information may be used to analyze performance or
            otherwise improve our Services, but it will be aggregated and
            anonymized so that personal information is not revealed. For
            property management data, we follow privacy law requirements when
            creating aggregated datasets.
          </ListItem>
        </List>

        <Header>When and With Whom We Share Information</Header>
        <TextSection>
          We may share information about you as follows:
        </TextSection>

        <ItalicsHeader>
          Referrals to Landlords, Agents, Contractors, Lenders, and Other
          Partners
        </ItalicsHeader>
        <TextSection>
          <strong>Affiliate & Referral Disclosure:</strong> From time to time,{" "}
          {APP_NAME} may receive compensation, referral fees, revenue share,
          commissions, or other consideration in connection with referrals or
          introductions made through the platform. Examples of referral
          relationships include landlords, property managers, real estate agents
          and brokers, contractors and handymen, maintenance vendors, lenders
          and loan officers, insurers, and other real estate professionals. This
          disclosure is provided for transparency and does not change how your
          information is handled under this Privacy Policy.
        </TextSection>
        <TextSection>
          With your direction or consent, we may share limited information with
          third-party professionals and vendors (collectively, "Partners") to
          fulfill a referral or introduction you request through the platform.
          Examples include landlords and property managers, real estate
          agents/brokers, contractors/handymen and maintenance vendors,
          lenders/mortgage brokers/loan officers, insurance providers,
          accountants, and attorneys.
        </TextSection>
        <List>
          <ListItem>
            <strong>Scope of information shared:</strong> We typically share
            only the minimum information reasonably necessary to facilitate a
            referral (for example, your name, contact details, and high-level
            property context). We do not share tenant personal information with
            Partners unless you specifically request it and it is legally
            permissible.
          </ListItem>
          <ListItem>
            <strong>Legal basis:</strong> We rely on your consent and/or our
            legitimate interest in fulfilling your directed request to be
            connected with a Partner. Where required by law, we will obtain
            explicit consent.
          </ListItem>
          <ListItem>
            <strong>Partner privacy practices:</strong> Partners are independent
            third parties with their own privacy policies and compliance
            obligations. Any information you provide directly to a Partner is
            governed by that Partner’s privacy policy, not this Privacy Policy.
          </ListItem>
          <ListItem>
            <strong>Compensation disclosures:</strong> {APP_NAME} may receive
            compensation, referral fees, revenue share, commissions, or other
            consideration in connection with referrals or introductions
            (including relationships with landlords, agents/brokers,
            contractors, lenders, and other real estate professionals). We do
            not sell personal information for monetary value, and any
            compensation we receive does not increase the price you pay to
            Partners. Where required by law, we provide additional disclosures
            at the time of referral.
          </ListItem>
        </List>

        <ItalicsHeader>Property Management and Legal Services</ItalicsHeader>
        <List>
          <ListItem>
            With your property manager or legal counsel who uses our platform to
            manage your properties and ensure legal compliance, consistent with
            applicable privacy laws and regulations.
          </ListItem>
          <ListItem>
            With other professionals involved in your property management, upon
            explicit direction from you or as required by law.
          </ListItem>
        </List>

        <ItalicsHeader>Tax Preparation and CPA Transmission</ItalicsHeader>
        <TextSection>
          When you use our tax preparation feature and submit your data, we
          transmit that information (property addresses, income, expenses, and
          your contact details) to your designated certified public accountant
          (CPA) via email, with you copied on the transmission. By submitting,
          you authorize this sharing. Your CPA is an independent third party
          with their own privacy practices. We do not control how your CPA uses
          or retains the data. Draft data stored in your browser's local storage
          is not transmitted until you explicitly submit.
        </TextSection>

        <ItalicsHeader>Safety and compliance with the law</ItalicsHeader>
        <List>
          <ListItem>
            If we have a good-faith belief that access, use, preservation or
            disclosure of the information is reasonably necessary to:
          </ListItem>
          <ListItem>
            Meet any applicable law, regulation, legal process or enforceable
            governmental request.
          </ListItem>
          <ListItem>
            Enforce applicable Terms of Service, including investigation of
            potential violations.
          </ListItem>
          <ListItem>
            Detect, prevent, or otherwise address fraud, security or technical
            issues.
          </ListItem>
          <ListItem>
            Protect against harm to the rights, property or safety of
            {APP_NAME}, our users, or the public as required or permitted by
            law.
          </ListItem>
        </List>

        <ItalicsHeader>Business transfers and corporate changes</ItalicsHeader>
        <TextSection>
          If {APP_NAME} is involved in a merger, acquisition or asset sale, we
          will continue to ensure the confidentiality of any personal
          information and give affected users notice before personal information
          is transferred or becomes subject to a different privacy policy. Any
          successor entity acquiring our business will be obligated to maintain
          the same protections for your personal information as specified in our
          privacy policy.
        </TextSection>

        <Header>Data Breach Notification</Header>
        <TextSection>
          In compliance with applicable privacy laws, we will:
        </TextSection>
        <List>
          <ListItem>
            Notify affected users without unreasonable delay and in no case
            later than required by applicable law following the discovery of a
            breach of personal information.
          </ListItem>
          <ListItem>
            Provide information regarding the breach, including a description of
            what happened, the types of information involved, steps individuals
            should take to protect themselves, what we are doing to investigate
            and mitigate the breach, and contact procedures for individuals to
            ask questions.
          </ListItem>
          <ListItem>
            Cooperate fully with law enforcement and regulatory authorities in
            fulfilling our obligations to notify affected individuals and
            authorities as required by law.
          </ListItem>
        </List>

        <Header>How Long We Retain Your Information</Header>
        <TextSection>
          We retain data until it is no longer necessary to provide our services
          to you, or until your account is deleted - whichever comes first. If
          you no longer want {APP_NAME} to use your information to provide the
          Services to you, you may delete your account.
        </TextSection>
        <TextSection>
          For personal information, we maintain records in accordance with
          applicable privacy laws and regulations, generally for as long as
          necessary to provide our services or as required by law. Retention
          periods may be extended if required by applicable laws or regulations.
        </TextSection>
        <TextSection>
          We also retain log files for internal analysis purposes. These log
          files are generally stored for a brief period of time, except in cases
          where they are used to improve service functionality or security, or
          where we are legally obligated to retain them for longer periods. We
          also retain information from accounts disabled for terms violations
          for at least a year to prevent repeat abuse or violations.
        </TextSection>

        <Header>Third party service providers</Header>
        <TextSection>
          Our Privacy Policy applies to all of the services offered by{" "}
          {APP_NAME}, but excludes services that have separate privacy policies
          that do not incorporate this Privacy Policy. We use third-party
          service providers primarily for email and analytics services.
        </TextSection>

        <ItalicsHeader>
          Examples of our third-party service providers include:
        </ItalicsHeader>
        <List>
          <ListItem>
            Mixpanel: we use Mixpanel to understand how our Services perform,
            and how you use them.
          </ListItem>
          <ListItem>
            Mailchimp: we use Mailchimp for email monitoring and automation.
          </ListItem>
          <ListItem>
            Mailtrap and similar transactional email providers: we may use these
            services to deliver system and internal notifications (for example,
            when feedback is submitted for review).
          </ListItem>
          <ListItem>
            AWS: we use Amazon Web Services for secure cloud storage and
            computing infrastructure.
          </ListItem>
          <ListItem>
            Google Firebase / Google Cloud: we may use Google services for
            authentication, databases, hosting, or related infrastructure in
            connection with the Services.
          </ListItem>
          <ListItem>
            Stripe: we use Stripe for payment processing.
          </ListItem>
          <ListItem>
            PayPal or other payment partners: we may offer additional payment
            methods where presented at checkout; those providers process
            payments under their own terms and privacy notices.
          </ListItem>
          <ListItem>
            AI and machine-learning providers: we may send prompts, property or
            account context, and related inputs to third-party AI model providers
            to power features such as assistants, messaging aids, and analysis
            tools, subject to our agreements with those providers.
          </ListItem>
          <ListItem>
            IP intelligence or geolocation services: we may use vendors that map
            IP addresses to approximate city, region, or country for security,
            analytics, and product improvement (including contextualizing
            feedback).
          </ListItem>
        </List>

        <TextSection>
          For service providers who may have access to personal information, we
          execute data processing agreements and implement controls to ensure
          they meet privacy compliance requirements.
        </TextSection>

        <TextSection>
          This Privacy Policy does not apply to, nor are we responsible for,
          third-party cookies, pixel tags, web beacons, or other tracking
          technologies, which are covered by such third parties' privacy
          policies. For more information, we encourage you to check the privacy
          policies of these third parties to learn about their privacy
          practices.
        </TextSection>

        <Header>Your Choices</Header>
        <TextSection>
          People have different personal privacy concerns. Our goal is to be
          clear about what information we collect, so that you can make
          meaningful choices about how it is used.
        </TextSection>

        <TextSection>
          Here are some of your choices related to personal privacy:
        </TextSection>
        <List>
          <ListItem>
            You may set your browser to block all cookies, including cookies
            associated with our services, or to indicate when a cookie is being
            set by us. However, it is important to remember that many of our
            services may not function properly if your cookies are disabled.
          </ListItem>
          <ListItem>
            If you no longer wish to use the Services or receive service-related
            communications (except for legally required notices), then you may
            delete your account.
          </ListItem>
          <ListItem>
            For landlords and property managers, you may request deletion of
            tenant information in accordance with applicable privacy laws and
            regulations.
          </ListItem>
        </List>

        <ItalicsHeader>Your Privacy Rights</ItalicsHeader>
        <TextSection>
          Depending on your location, you may have certain rights under
          applicable privacy laws. These rights may include:
        </TextSection>
        <List>
          <ListItem>
            <strong>Right to Access:</strong> You have the right to view and
            obtain copies of your personal information.
          </ListItem>
          <ListItem>
            <strong>Right to Amend:</strong> You have the right to request
            corrections to your personal information.
          </ListItem>
          <ListItem>
            <strong>Right to Deletion:</strong> You have the right to request
            deletion of your personal information.
          </ListItem>
          <ListItem>
            <strong>Right to Portability:</strong> You have the right to request
            a copy of your personal information in a portable format.
          </ListItem>
          <ListItem>
            <strong>Right to Opt-Out:</strong> You have the right to opt-out of
            certain data processing activities.
          </ListItem>
        </List>
        <TextSection>
          To exercise these rights, please contact us at {APP_SUPPORT_EMAIL}. We
          will work with you to fulfill any valid requests regarding your
          personal information.
        </TextSection>
        <TextSection>
          While you may have provided consent to use our property management
          services, this consent is limited to the specific purposes outlined in
          our privacy policy. Your consent permits us to provide property
          management services and to store related personal information, but it
          does not waive your fundamental privacy rights. You retain the right
          to revoke your consent by contacting us, though this will not affect
          actions taken prior to the revocation.
        </TextSection>

        <TextSection>
          By using our platform and submitting content (including messages,
          notes, and uploaded documents), you acknowledge that this information
          may be used by {APP_NAME} for platform improvement, marketing,
          demonstration purposes, and other business purposes in accordance with
          our privacy policy.
        </TextSection>

        <TextSection>
          If your personal information is wrong, we strive to give you ways to
          update it quickly or to remove it, unless we are required to retain
          that information for legitimate business or legal purposes.
        </TextSection>

        <TextSection>
          Where we can provide information access and correction, we will do so
          for free, except where it would require a disproportionate effort. We
          aim to maintain our services in a manner that protects information
          from accidental or malicious destruction. Because of this, after you
          delete information from our services, we may not immediately delete
          residual copies from our active servers and may not remove information
          from our backup systems. Please note that we may reject requests that
          are unreasonably repetitive, require disproportionate technical effort
          (for example, developing a new system or fundamentally changing an
          existing practice), risk the privacy of others, or would be extremely
          impractical (for instance, requests concerning information residing on
          backup tapes).
        </TextSection>

        <ItalicsHeader>Withdrawing consent</ItalicsHeader>
        <TextSection>
          You have the right to withdraw your consent to our processing of your
          information and your use of our Services. For example:
        </TextSection>
        <List>
          <ListItem>
            You can withdraw consent from email marketing or notifications by
            using the unsubscribe link in such communications.
          </ListItem>
          <ListItem>
            You can choose to withdraw your consent to our processing of your
            data and your use of the Services by closing your account, by using
            the "Delete account" option in your {APP_NAME} account settings.
          </ListItem>
        </List>

        <TextSection>
          Depending on your location, you can also email{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a> to
          request that your personal information be deleted, excluding
          information we are legally required to retain.
        </TextSection>

        <Header id="consumer-health">Consumer Health Data Privacy</Header>
        <TextSection>
          For users in states with consumer health data privacy laws (including
          Washington (My Health My Data Act, MHMDA), Nevada (SB 370),
          Connecticut, and other states with similar requirements), we comply
          with applicable consumer health data rules. We do not sell consumer
          health data. If you share health-related information in chat or other
          features, we may ask for your explicit consent before processing it.
          You have the right to know what consumer health data we collect, how
          it is used, and to withdraw consent or request deletion. To exercise
          these rights or for questions about consumer health data, contact us
          at <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a>.
        </TextSection>

        <Header>Security</Header>
        <TextSection>
          Your personal information is important to you, and protecting that
          information is important to us. We take reasonable measures to protect{" "}
          {APP_NAME} and our users from unauthorized access, alteration,
          disclosure, or loss of information we hold, including administrative,
          technical, and physical safeguards.
        </TextSection>

        <TextSection>
          For personal information, we implement security measures that meet or
          exceed industry standards, including:
        </TextSection>
        <List>
          <ListItem>
            <strong>Encryption:</strong> All personal information is encrypted
            in transit and at rest using industry-standard protocols and
            algorithms.
          </ListItem>
          <ListItem>
            <strong>Access Controls:</strong> Role-based access controls, unique
            user authentication, automatic logout, and other technical
            safeguards.
          </ListItem>
          <ListItem>
            <strong>Intrusion Detection and Prevention:</strong> Real-time
            monitoring for unauthorized access attempts.
          </ListItem>
          <ListItem>
            <strong>Data Backups:</strong> Regular encrypted backups with secure
            storage and retention policies.
          </ListItem>
          <ListItem>
            <strong>Disaster Recovery:</strong> Comprehensive disaster recovery
            and business continuity plans.
          </ListItem>
          <ListItem>
            <strong>Physical Safeguards:</strong> Our data centers maintain
            robust physical security measures including access controls,
            surveillance, and environmental protections.
          </ListItem>
          <ListItem>
            <strong>Staff Training:</strong> Regular security and privacy
            compliance training for all staff with access to personal
            information.
          </ListItem>
        </List>

        <TextSection>
          Your account information is protected by a password. It is important
          that you protect against unauthorized access to your account and
          information by choosing your password carefully and by keeping your
          password and device secure, such as by logging out after using the
          Services.
        </TextSection>

        <TextSection>
          We follow generally accepted standards to protect the personal
          information submitted to us, both during transmission and after it is
          received. Here are some of the security measures we take:
        </TextSection>
        <List>
          <ListItem>
            We encrypt many of our services and certain information using secure
            socket layer technology (SSL).
          </ListItem>
          <ListItem>
            We review our information collection, storage and processing
            practices, including physical security measures, to guard against
            unauthorized access to systems.
          </ListItem>
          <ListItem>
            We restrict access to personal information to {APP_NAME} employees,
            contractors and agents who need to know that information in order to
            process it for us, and who are subject to strict contractual
            confidentiality obligations and may be disciplined or terminated if
            they fail to meet these obligations.
          </ListItem>
        </List>

        <TextSection>
          Despite our robust security measures, no method of transmission over
          the internet or method of electronic storage is 100% secure, and we
          cannot guarantee absolute security of the transmission or storage of
          your information. We promptly investigate and address any potential
          security incidents and will notify affected parties as required by
          applicable law.
        </TextSection>

        <Header>Changes to This Policy</Header>
        <TextSection>
          Our Privacy Policy may change from time to time, at any time. We will
          not reduce your rights under this Privacy Policy without your explicit
          consent. We will post any privacy policy changes on this page and, if
          the changes are significant, we will provide a more prominent notice
          (including, for certain services, email notification of privacy policy
          changes).
        </TextSection>

        <Header>Contact</Header>
        <TextSection>
          Please contact our Privacy Officer at{" "}
          <a href={`mailto:${APP_SUPPORT_EMAIL}`}>{APP_SUPPORT_EMAIL}</a> with
          any questions or concerns regarding this Privacy Policy or our privacy
          compliance practices.
        </TextSection>

        <TextSection>
          We regularly review our compliance with our Privacy Policy. When we
          receive formal written complaints, we will contact the person who made
          the complaint to follow up. We work with the appropriate regulatory
          authorities, including local data protection authorities, to resolve
          any complaints regarding the transfer or handling of personal data
          that we cannot resolve with our users directly.
        </TextSection>

        <Header>Authorized Uses of Your Information</Header>
        <TextSection>
          When landlords and property managers use our platform, they should
          have obtained appropriate consent to process tenant information for
          the specific purpose of property management. This consent typically
          includes:
        </TextSection>
        <List>
          <ListItem>
            Permission to collect and process tenant information for rent
            tracking, lease management, and property maintenance
          </ListItem>
          <ListItem>
            Consent for {APP_NAME} to process this information to provide
            property management services
          </ListItem>
          <ListItem>
            Understanding that tenant information will be stored securely within
            our system in accordance with applicable privacy laws
          </ListItem>
          <ListItem>
            Acknowledgment that the services are for property management
            purposes and not guarantees of specific outcomes
          </ListItem>
        </List>
        <TextSection>
          At no point do we ask tenants to waive their privacy rights in order
          to use our Services. Our platform is designed to operate within the
          framework of proper privacy-compliant consent while preserving all
          individual rights under applicable law.
        </TextSection>
      </TextContainer>
    </Container>
  );
}

const Container = styled.div`
  color: black;
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  text-align: left;
  width: 80%;
  margin: auto;
  overflow: hidden;
  margin-bottom: 40px;
  margin-top: 3rem;

  a {
    color: #1a73e8;
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: #0d47a1;
    text-decoration: underline;
  }
`;

const TextContainer = styled.div`
  height: 100%;
  overflow: unset !important;
  padding-right: 5%;
  padding-bottom: 32px;
`;

const Header = styled.div`
  font-size: 13px;
  font-weight: bold;
  line-height: 20.8px;
  margin-top: 24px;
`;

const MainHeader = styled.div`
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 18px;
`;

const List = styled.ul`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 18px;
`;

const ListItem = styled.li`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 10px;
`;

const TextSection = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 20.8px;
  margin-top: 18px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;

const ItalicsHeader = styled.div`
  font-size: 13px;
  font-style: italic;
  line-height: 20.8px;
  margin-top: 24px;
`;
